import {
  getIndexBelow,
  getIndexBetween,
  IndexKey,
  PageRecordType,
  TLDOCUMENT_ID,
  TLPageId,
  useEditor,
  useValue,
} from "tldraw"
import { useEffect } from "react"
import { indexOf } from "lodash"
import { Plus } from "lucide-react"
import { PageThumb } from "./PageThumb"
import { Layer } from "../../shared/Layer"
import { ToolbarIconButton } from "./ToolbarButton"
import { styled } from "@mui/material"
import { Expr } from "../../shared/Expr"
import { Parameter } from "../../shared/Parameter"

type PageScrollerProps = {
  onStickerDrag: (
    pageId: TLPageId,
    x: number,
    y: number,
    width: number,
    height: number,
  ) => void
}

const PageScrollerDiv = styled("div")({
  display: "flex",
  overflow: "auto",
  padding: 5,
})

export function PageScroller({ onStickerDrag }: PageScrollerProps) {
  const editor = useEditor()

  const currentPageId = useValue("page id", () => editor.getCurrentPageId(), [
    editor,
  ])
  const pages = useValue("pages", () => editor.getPages(), [editor])

  // store the current page id in the document meta
  useEffect(() => {
    editor.store.update(TLDOCUMENT_ID, (doc) => ({
      ...doc,
      meta: { ...doc.meta, currentPageId },
    }))
  }, [currentPageId])

  function onPageAddClick(index?: IndexKey) {
    //console.log("onPageAddClick", index)

    const newPageId = PageRecordType.createId()

    const pagesLength = editor.getPages().length

    const timeParameter: Parameter = {
      name: "Time",
      type: "number",
    }

    editor.createPage({
      id: newPageId,
      name: `Page ${pagesLength + 1}`,
      index: index,
      meta: {
        currentLayer: "layer1",
        layers: {
          layer1: Layer.layer1,
        },
        parameters: {
          // time: timeParameter,
        },
        parameterValues: {
          // time: Expr.lit(Date.now()),
        },
        origin: { x: 0, y: 0 },
        //currentMarkerRadius: 10,
      },
    })
    editor.setCurrentPage(newPageId)
    editor.setCurrentTool("draw-stroke")
  }

  function handleSelectPage(pageId: TLPageId) {
    editor.setCurrentPage(pageId)
    editor.selectNone()
  }

  function handleStickerDrag(
    pageId: TLPageId,
    x: number,
    y: number,
    width: number,
    height: number,
  ) {
    onStickerDrag(pageId, x, y, width, height)
  }

  return (
    <PageScrollerDiv>
      {pages.flatMap((page, idx) => {
        const index = pages[idx - 1]
          ? getIndexBetween(pages[idx - 1].index, page.index)
          : getIndexBelow(pages[idx].index)

        const getPage = (pageId: TLPageId) =>
          pages.find((page) => page.id === pageId)

        const draggingAvailable =
          idx < indexOf(pages, getPage(currentPageId)!) ? true : false
        return [
          <PageThumbSeparator
            key={page.id + "-s"}
            onClick={() => onPageAddClick(index)}
          />,
          <PageThumb
            key={page.id}
            pageId={page.id}
            selected={page.id === currentPageId}
            onSelect={handleSelectPage}
            onStickerDrag={(pageId, x, y, width, height) =>
              draggingAvailable
                ? handleStickerDrag(pageId, x, y, width, height)
                : undefined
            }
          />,
        ]
      })}
      <div>
        <ToolbarIconButton icon={Plus} onClick={() => onPageAddClick()} />
      </div>
    </PageScrollerDiv>
  )
}

const PageThumbSeparatorDiv = styled("div")({
  width: 12,
  padding: 0,
  border: "4px solid transparent",
  borderStyle: "none solid none solid",
  "&:hover": {
    background: "#47f",
    cursor: "pointer",
    backgroundClip: "padding-box",
  },
})

type PageThumbSeparatorProps = {
  onClick: () => void
}

function PageThumbSeparator({ onClick }: PageThumbSeparatorProps) {
  return <PageThumbSeparatorDiv title="Add Page" onClick={onClick} />
}
